import '@shoelace-style/shoelace/dist/themes/light.css';
import { apiCall, ApiError } from './api';
import { completeLogin, getUser, login, logout, onAuthEvents } from './auth';

// Extend window interface
declare global {
  interface Window {
    subscriptionStatus?: SubscriptionStatus;
  }
}

interface Feed {
  id?: string;
  slot: number;
  location: string | null;
  latitude?: number | null;
  longitude?: number | null;
  grid_last_refreshed?: string | null;
  ical_url?: string;
  rss_url?: string;
  // Statistics fields
  last_refreshed?: string | null;
  feed_tier?: string;
  ical_access_count?: number;
  rss_access_count?: number;
  ical_last_accessed?: string | null;
  rss_last_accessed?: string | null;
  refresh_count?: number;
}

interface SubscriptionStatus {
  is_subscribed: boolean;
  subscription_type: string;
  max_feeds: number;
  max_forecast_days: number;
  has_historical: boolean;
}

interface HourlyData {
  when: string;
  temp_f: number;
  condition: string;
  precip_chance_pct: number;
}

interface ForecastData {
  resource_id: string;
  created_at: string;
  updated_at: string;
  username: string;
  location: string;
  feed_id: string;
  forecast_date: string;
  summary: string;
  high_f: number;
  low_f: number;
  max_precip_chance_pct: number;
  hourly: HourlyData[];
  source: string;
}

interface FeedForecast {
  feed_id: string;
  location: string;
  forecast_date: string;
  latest?: ForecastData;
  original?: ForecastData;
}

// Setup auth event handlers
onAuthEvents();

// DOM elements
const els = {
  landingPage: document.getElementById('landing-page') as HTMLDivElement,
  appPage: document.getElementById('app-page') as HTMLDivElement,
  landingLogin: document.getElementById('landing-login') as HTMLElement,
  landingLogo: document.getElementById('landing-logo') as HTMLImageElement,
  logoutBtn: document.getElementById('logout') as HTMLElement,
  userEmail: document.getElementById('user-email') as HTMLSpanElement,
  feedsContainer: document.getElementById('feeds-container') as HTMLDivElement,
  feedsNotice: document.getElementById('feeds-notice') as HTMLDivElement,
  showSignup: document.getElementById('show-signup') as HTMLElement,
  signupModal: document.getElementById('signup-modal') as HTMLElement & { show(): void; hide(): void }, // Shoelace dialog element
  signupEmail: document.getElementById('signup-email') as HTMLElement,
  signupMessageContainer: document.getElementById('signup-message-container') as HTMLDivElement,
  submitSignup: document.getElementById('submit-signup') as HTMLElement,
  cancelSignup: document.getElementById('cancel-signup') as HTMLElement,
  subscriptionBadge: document.getElementById('subscription-badge') as HTMLSpanElement,
  subscriptionAction: document.getElementById('subscription-action') as HTMLElement,
  infoContent: document.getElementById('info-content') as HTMLDivElement,
  cancellationModal: document.getElementById('cancellation-modal') as HTMLElement & { show(): void; hide(): void },
  feedSelection: document.getElementById('feed-selection') as HTMLDivElement,
  cancelCancellation: document.getElementById('cancel-cancellation') as HTMLElement,
  confirmCancellation: document.getElementById('confirm-cancellation') as HTMLElement,
};

// Handle redirect callback
if (window.location.pathname.endsWith('/callback')) {
  await completeLogin();
  history.replaceState({}, '', '/');
}

// Check if user wants to retry upgrade (from payment cancelled page)
if (window.sessionStorage.getItem('retryUpgrade') === 'true') {
  window.sessionStorage.removeItem('retryUpgrade');
  // Delay to allow app to initialize
  setTimeout(() => {
    const subscriptionBtn = document.getElementById('subscription-action');
    if (subscriptionBtn && subscriptionBtn.getAttribute('data-subscribed') === 'false') {
      subscriptionBtn.click();
    }
  }, 1000);
}

// Event listeners
els.landingLogin.addEventListener('click', () => login());
els.logoutBtn.addEventListener('click', async () => {
  await logout();
  window.location.reload();
});

// Helper function to show signup message
function showSignupMessage(message: string, variant: 'success' | 'warning' | 'danger' | 'primary' = 'primary', isHtml: boolean = false) {
  // Clear previous message
  els.signupMessageContainer.innerHTML = '';
  
  // Create new alert element
  const alert = document.createElement('sl-alert');
  alert.setAttribute('variant', variant);
  alert.setAttribute('open', '');
  
  // Set icon based on variant
  const iconMap = {
    success: 'check2-circle',
    warning: 'exclamation-triangle',
    danger: 'exclamation-octagon',
    primary: 'info-circle',
  };
  
  alert.innerHTML = `
    <sl-icon slot="icon" name="${iconMap[variant]}"></sl-icon>
    ${isHtml ? message : `<span>${message}</span>`}
  `;
  
  els.signupMessageContainer.appendChild(alert);
  els.signupMessageContainer.style.display = 'block';
}

// Signup modal listeners
els.showSignup.addEventListener('click', () => {
  // Reset the modal state when opening
  (els.signupEmail as HTMLInputElement).value = '';
  els.signupMessageContainer.style.display = 'none';
  els.signupMessageContainer.innerHTML = '';
  (els.submitSignup as HTMLElement & { disabled: boolean }).disabled = false;
  els.submitSignup.textContent = 'Create Account';
  els.signupModal.show();
});

els.cancelSignup.addEventListener('click', () => {
  els.signupModal.hide();
  (els.signupEmail as HTMLInputElement).value = '';
  els.signupMessageContainer.style.display = 'none';
  els.signupMessageContainer.innerHTML = '';
});

els.submitSignup.addEventListener('click', async () => {
  const email = (els.signupEmail as HTMLInputElement).value;
  if (!email) {
    showSignupMessage('Please enter your email address.', 'warning');
    return;
  }
  
  // Show loading state
  const submitBtn = els.submitSignup as HTMLElement & { disabled: boolean; loading: boolean };
  submitBtn.disabled = true;
  submitBtn.loading = true;
  els.submitSignup.textContent = 'Creating account...';
  
  try {
    await apiCall<{message: string; status: string}>('POST', '/api/feeds/signup', {
      json: { email },
    });
    
    // Show success message in modal
    const successMessage = `
      <strong>Account created successfully!</strong><br>
      <div style="margin-top: 0.5rem;">
        Please check your email at <strong>${email}</strong> for your temporary password.
        You can then sign in using the "Sign In" button.
      </div>
    `;
    showSignupMessage(successMessage, 'success', true);
    
    // Disable the submit button and change text
    submitBtn.loading = false;
    els.submitSignup.textContent = 'Account Created';
    
    // Clear the email field
    (els.signupEmail as HTMLInputElement).value = '';
    
    // Optional: Auto-close after a delay
    setTimeout(() => {
      els.signupModal.hide();
      els.signupMessageContainer.style.display = 'none';
      els.signupMessageContainer.innerHTML = '';
      submitBtn.disabled = false;
      els.submitSignup.textContent = 'Create Account';
    }, 8000); // Close after 8 seconds
    
  } catch (error: unknown) {
    // Try to extract error message
    let errorMessage = 'Failed to create account. Please try again.';
    
    if (error instanceof ApiError) {
      // Handle 422 validation errors
      if (error.status === 422 && error.body && typeof error.body === 'object' && 'detail' in error.body) {
        const detail = error.body.detail;
        if (Array.isArray(detail) && detail.length > 0) {
          // Extract the first validation error message
          const firstError = detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          }
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      } else if (error.body && typeof error.body === 'object' && 'detail' in error.body) {
        errorMessage = error.body.detail as string;
      }
    }
    
    showSignupMessage(errorMessage, 'danger');
    
    // Re-enable the submit button
    submitBtn.loading = false;
    submitBtn.disabled = false;
    els.submitSignup.textContent = 'Create Account';
  }
});

// Cancellation modal listeners
els.cancelCancellation.addEventListener('click', () => {
  els.cancellationModal.hide();
});

els.confirmCancellation.addEventListener('click', async () => {
  const selectedFeed = document.querySelector('input[name="keep-feed"]:checked') as HTMLInputElement;
  const keepFeedId = selectedFeed?.value || null;
  
  try {
    await apiCall('POST', '/api/feeds/unsubscribe', {
      json: { keep_feed_id: keepFeedId },
    });
    showToast('Subscription cancelled. Your selected feed has been preserved.', 'success');
    els.cancellationModal.hide();
    await sleep(1000);
    await initApp();  // Reload to update UI
  } catch {
    showToast('Failed to cancel subscription', 'danger');
  }
});

// Subscription action listener (will be configured based on status)
els.subscriptionAction.addEventListener('click', async () => {
  const isSubscribed = els.subscriptionAction.getAttribute('data-subscribed') === 'true';
  
  if (isSubscribed) {
    // Open Stripe Customer Portal for billing management
    try {
      const currentUrl = window.location.origin;
      const returnUrl = `${currentUrl}/`;
      
      const response = await apiCall<{portal_url: string}>('POST', '/api/payments/customer-portal', {
        json: { return_url: returnUrl },
      });
      
      // Redirect to Stripe Customer Portal
      window.location.href = response.portal_url;
    } catch {
      showToast('Failed to open billing portal. Please try again.', 'danger');
    }
  } else {
    // Check if feed 1 is configured before allowing upgrade
    try {
      const feeds = await apiCall<Feed[]>('GET', '/api/feeds');
      const feed1 = feeds.find(f => f.slot === 1);
      
      if (!feed1 || !feed1.location) {
        showToast('Please configure Feed Slot 1 with a location first before upgrading to Premium', 'warning');
        // Optionally scroll to the first feed slot
        const firstFeedCard = document.querySelector('.feed-card');
        if (firstFeedCard) {
          firstFeedCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
      
      // Create Stripe Checkout session
      try {
        // Get current URL for success/cancel redirects
        const currentUrl = window.location.origin;
        const successUrl = `${currentUrl}/payment-success`;
        const cancelUrl = `${currentUrl}/payment-cancelled`;
        
        const response = await apiCall<{checkout_url: string; session_id: string}>('POST', '/api/payments/create-checkout-session', {
          json: {
            success_url: successUrl,
            cancel_url: cancelUrl,
          },
        });
        
        // Redirect to Stripe Checkout
        window.location.href = response.checkout_url;
      } catch (error) {
        if ((error as Error).message?.includes('already has an active subscription')) {
          showToast('You already have an active subscription!', 'warning');
        } else {
          showToast('Failed to create checkout session. Please try again.', 'danger');
        }
      }
      
    } catch {
      showToast('Failed to check feed configuration. Please try again.', 'danger');
    }
  }
});

// Initialize app
initApp();

async function initApp() {
  const user = await getUser();
  if (user) {
    els.landingPage.style.display = 'none';
    els.appPage.style.display = 'block';
    els.userEmail.textContent = user.profile?.email || 'User';
    
    // Load subscription status
    try {
      const status = await apiCall<SubscriptionStatus>('GET', '/api/feeds/subscription-status');
      updateSubscriptionUI(status);
    } catch (error) {
      console.error('Failed to load subscription status:', error);
      // Default to free tier
      updateSubscriptionUI({
        is_subscribed: false,
        subscription_type: 'free',
        max_feeds: 3,
        max_forecast_days: 3,
        has_historical: false,
      });
    }
    
    await loadFeeds();
  } else {
    els.landingPage.style.display = 'flex';
    els.appPage.style.display = 'none';
  }
}

// Update UI based on subscription status
function updateSubscriptionUI(status: SubscriptionStatus) {
  // Update subscription badge
  if (status.is_subscribed) {
    els.subscriptionBadge.innerHTML = '<sl-badge variant="success">Premium</sl-badge>';
    els.subscriptionAction.innerHTML = '<sl-icon name="credit-card" slot="prefix"></sl-icon><span style="display: inline-block; margin-left: 0.25rem;">Manage Billing</span>';
    els.subscriptionAction.setAttribute('variant', 'primary');
    els.subscriptionAction.setAttribute('data-subscribed', 'true');
  } else {
    els.subscriptionBadge.innerHTML = '<sl-badge variant="neutral">Free</sl-badge>';
    els.subscriptionAction.innerHTML = '<sl-icon name="star-fill" slot="prefix"></sl-icon><span style="display: inline-block; margin-left: 0.25rem;">Upgrade to Premium</span>';
    els.subscriptionAction.setAttribute('variant', 'success');
    els.subscriptionAction.setAttribute('data-subscribed', 'false');
  }
  els.subscriptionAction.style.display = 'inline-block';
  
  // Update info content with side-by-side comparison
  els.infoContent.innerHTML = `
    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; margin-bottom: 1rem;">
      <div style="${!status.is_subscribed ? 'opacity: 1; border: 2px solid #667eea;' : 'opacity: 0.6;'} padding: 1rem; background: #f8f9fa; border-radius: 8px;">
        <h4 style="margin-top: 0; color: #333; font-size: 1rem;">⭐ Basic (Free)</h4>
        <ul style="margin: 0.5rem 0; padding-left: 1.2rem; font-size: 0.875rem;">
          <li><strong>1 location</strong> feed</li>
          <li><strong>3-day</strong> forecast</li>
          <li>Sign-in once every 6 months to keep account active</li>
        </ul>
      </div>
      <div style="${status.is_subscribed ? 'border: 2px solid #28a745;' : 'opacity: 0.8;'} padding: 1rem; background: ${status.is_subscribed ? '#d4edda' : '#f8f9fa'}; border-radius: 8px;">
        <h4 style="margin-top: 0; color: #333; font-size: 1rem;">🌟 Premium</h4>
        <ul style="margin: 0.5rem 0; padding-left: 1.2rem; font-size: 0.875rem;">
          <li><strong>3 location</strong> feeds</li>
          <li><strong>7-day</strong> forecast</li>
          <li>Up to <strong>30-day</strong> forecast history</li>
          <li>No sign-in required</li>
        </ul>
      </div>
    </div>
    ${!status.is_subscribed ? `
      <p style="padding: 0.75rem; background: #fff3cd; border-radius: 6px; color: #856404; text-align: center; margin: 0;">
        🌟 <strong>Upgrade to Premium</strong> for extended forecasts and multiple locations!
      </p>` : `
      <p style="padding: 0.75rem; background: #d4edda; border-radius: 6px; color: #155724; text-align: center; margin: 0;">
        ✅ <strong>Premium Active</strong> - Enjoying all premium features!
      </p>`
}
  `;
  
  // Store status for use in feed rendering
  window.subscriptionStatus = status;
}

// Load user's feeds
async function loadFeeds() {
  try {
    const feeds = await apiCall<Feed[]>('GET', '/api/feeds');
    renderFeeds(feeds);
  } catch (error) {
    console.error('Failed to load feeds:', error);
    renderFeeds([]);
  }
}

// Render feed cards
function renderFeeds(feeds: Feed[]) {
  els.feedsContainer.innerHTML = '';
  
  // Clear any existing notices first
  els.feedsNotice.innerHTML = '';

  // Check if we need to show upgrade/downgrade notices
  const status = window.subscriptionStatus as SubscriptionStatus;
  const hasBasicFeeds = feeds.some(f => f.feed_tier === 'basic');
  const hasPremiumFeeds = feeds.some(f => f.feed_tier === 'premium');
  
  // Show notice if user status doesn't match feed tiers
  if (status) {
    let noticeHtml = '';
    
    if (status.is_subscribed && hasBasicFeeds) {
      noticeHtml = `
        <div style="background: #d4edda; border: 1px solid #c3e6cb; border-radius: 8px; padding: 1rem; margin-bottom: 1rem; color: #155724;">
          <sl-icon name="info-circle" style="margin-right: 0.5rem;"></sl-icon>
          <strong>Upgrade Notice:</strong> Your basic feeds will be upgraded to premium on the next refresh, giving you 7-day forecasts and historical data.
        </div>
      `;
    } else if (!status.is_subscribed && hasPremiumFeeds) {
      noticeHtml = `
        <div style="background: #fff3cd; border: 1px solid #ffeeba; border-radius: 8px; padding: 1rem; margin-bottom: 1rem; color: #856404;">
          <sl-icon name="exclamation-triangle" style="margin-right: 0.5rem;"></sl-icon>
          <strong>Downgrade Notice:</strong> Your premium feeds will be downgraded to basic (3-day forecast only) on the next refresh. Subscribe to keep premium features!
        </div>
      `;
    }
    
    if (noticeHtml) {
      const noticeDiv = document.createElement('div');
      noticeDiv.innerHTML = noticeHtml;
      els.feedsNotice.appendChild(noticeDiv.firstElementChild as HTMLElement);
    }
  }

  // Create a map of feeds by slot number
  const feedsBySlot = new Map<number, Feed>();
  feeds.forEach(feed => {
    if (feed.slot) {
      feedsBySlot.set(feed.slot, feed);
    }
  });

  // Ensure we always show 3 feed slots in the correct positions
  for (let slot = 1; slot <= 3; slot++) {
    const feed = feedsBySlot.get(slot) || { slot: slot, location: null };
    const isDisabled = !status.is_subscribed && slot > 1;
    const card = createFeedCard(feed, slot, isDisabled);
    els.feedsContainer.appendChild(card);
  }
}

// Fetch forecast for a feed
async function fetchFeedForecast(feedId: string, date: string): Promise<FeedForecast | null> {
  try {
    const response = await apiCall<FeedForecast>('GET', `/api/forecast-for-feed/${feedId}/${date}`);
    return response;
  } catch (error) {
    console.error('Failed to fetch forecast:', error);
    return null;
  }
}

// Load and display forecast for a feed
async function loadFeedForecast(feedId: string, slot: number) {
  const container = document.getElementById(`forecast-container-${slot}`);
  if (!container) return;

  // Get today's date in YYYY-MM-DD format
  const today = new Date();
  const dateStr = today.toISOString().split('T')[0];

  // Show loading state
  container.innerHTML = `
    <div style="text-align: center; padding: 1rem;">
      <sl-spinner style="font-size: 1.5rem;"></sl-spinner>
      <p style="margin-top: 0.5rem; color: #666; font-size: 0.875rem;">Loading today's forecast...</p>
    </div>
  `;

  const forecast = await fetchFeedForecast(feedId, dateStr);
  
  if (!forecast || ! forecast.latest) {
    container.innerHTML = `
      <div style="text-align: center; padding: 1rem; color: #999;">
        <sl-icon name="cloud-slash" style="font-size: 2rem; margin-bottom: 0.5rem;"></sl-icon>
        <p style="font-size: 0.875rem;">Unable to load or no data yet available.</p>
      </div>
    `;
    return;
  }

  // Display the forecast using the latest data
  const latest = forecast.latest;
  const weatherEmoji = getWeatherEmoji(latest.summary || '');
  const hourly = latest.hourly || [];
  
  // Extract summary text without emoji
  const summaryText = latest.summary ? latest.summary.replace(/[^\w\s]/gi, '').trim() : 'N/A';
  
  container.innerHTML = `
    <div style="background: linear-gradient(135deg, #667eea15 0%, #764ba215 100%); border-radius: 8px; padding: 1rem;">
      <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem;">
        <div>
          <h4 style="margin: 0; color: #333; font-size: 0.95rem; font-weight: 600;">Today's Forecast</h4>
          <p style="margin: 0.25rem 0 0 0; color: #666; font-size: 0.75rem;">${today.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</p>
        </div>
        <div style="text-align: right;">
          <div style="font-size: 2rem; margin: 0;">${weatherEmoji}</div>
        </div>
      </div>
      
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; margin-bottom: 0.75rem;">
        <div style="background: white; padding: 0.5rem; border-radius: 6px;">
          <div style="color: #666; font-size: 0.7rem; margin-bottom: 0.25rem;">High / Low</div>
          <div style="font-weight: 600; color: #333;">
            ${latest.high_f ? Math.round(latest.high_f) : '--'}°F / ${latest.low_f ? Math.round(latest.low_f) : '--'}°F
          </div>
        </div>
        <div style="background: white; padding: 0.5rem; border-radius: 6px;">
          <div style="color: #666; font-size: 0.7rem; margin-bottom: 0.25rem;">Conditions</div>
          <div style="font-weight: 600; color: #333; font-size: 0.875rem;">
            ${summaryText}
          </div>
        </div>
      </div>

      ${latest.max_precip_chance_pct !== undefined ? `
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; margin-bottom: 0.75rem;">
          <div style="background: white; padding: 0.5rem; border-radius: 6px;">
            <div style="color: #666; font-size: 0.7rem; margin-bottom: 0.25rem;">Max Precipitation</div>
            <div style="font-weight: 600; color: #333;">
              ${Math.round(latest.max_precip_chance_pct)}%
            </div>
          </div>
          <div style="background: white; padding: 0.5rem; border-radius: 6px;">
            <div style="color: #666; font-size: 0.7rem; margin-bottom: 0.25rem;">Source</div>
            <div style="font-weight: 600; color: #333;">
              ${latest.source || 'N/A'}
            </div>
          </div>
        </div>
      ` : ''}

      ${hourly.length > 0 ? `
        <div style="background: white; border-radius: 6px; padding: 0.5rem; overflow-x: auto;">
          <div style="color: #666; font-size: 0.7rem; margin-bottom: 0.5rem;">Hourly Forecast</div>
          <div style="display: flex; gap: 0.75rem; min-width: max-content;">
            ${hourly.slice(0, 8).map((h) => `
              <div style="text-align: center; padding: 0.25rem;">
                <div style="font-size: 0.65rem; color: #666; margin-bottom: 0.25rem;">
                  ${new Date(h.when).toLocaleTimeString('en-US', { hour: 'numeric' })}
                </div>
                <div style="font-size: 1.2rem;">${getWeatherEmoji(h.condition)}</div>
                <div style="font-size: 0.75rem; font-weight: 600; color: #333; margin-top: 0.25rem;">
                  ${Math.round(h.temp_f)}°F
                </div>
                ${h.precip_chance_pct > 0 ? `
                  <div style="font-size: 0.6rem; color: #4A90E2; margin-top: 0.1rem;">
                    ${h.precip_chance_pct}%
                  </div>
                ` : ''}
              </div>
            `).join('')}
          </div>
        </div>
      ` : ''}
    </div>
  `;
}

// Get weather emoji based on description
function getWeatherEmoji(description: string): string {
  if (!description) return '☁️';
  const desc = description.toLowerCase();
  
  if (desc.includes('sun') || desc.includes('clear')) return '☀️';
  if (desc.includes('rain') || desc.includes('shower')) return '🌧️';
  if (desc.includes('storm') || desc.includes('thunder')) return '⛈️';
  if (desc.includes('snow')) return '❄️';
  if (desc.includes('fog') || desc.includes('mist')) return '🌫️';
  if (desc.includes('cloud') && desc.includes('part')) return '⛅';
  if (desc.includes('cloud')) return '☁️';
  if (desc.includes('wind')) return '💨';
  
  return '☁️';
}

// Create a feed card element
function createFeedCard(feed: Feed, slot: number, isDisabled: boolean = false): HTMLDivElement {
  const card = document.createElement('div');
  card.className = 'feed-card';
  
  if (isDisabled) {
    card.style.opacity = '0.6';
    card.style.position = 'relative';
  }

  if (!feed.location) {
    // Empty slot
    if (isDisabled) {
      // Disabled slot for basic users
      card.innerHTML = `
        <div class="feed-header">
          <div class="feed-title">Feed Slot ${slot}</div>
          <sl-badge variant="warning">Premium Only</sl-badge>
        </div>
        <div class="empty-feed">
          <sl-icon name="lock" style="font-size: 3rem; color: #999; margin-bottom: 1rem;"></sl-icon>
          <p style="margin-bottom: 1rem; color: #666;">Upgrade to Premium to unlock this slot</p>
          <sl-button variant="success" size="medium" style="margin-top: 1rem; width: 100%; max-width: 250px;" onclick="document.getElementById('subscription-action').click()">
            <sl-icon name="star-fill" slot="prefix"></sl-icon>
            Upgrade to Premium
          </sl-button>
        </div>
        <div style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(255,255,255,0.3); border-radius: 12px; pointer-events: none;"></div>
      `;
    } else {
      card.innerHTML = `
        <div class="feed-header">
          <div class="feed-title">Feed Slot ${slot}</div>
          <div class="status-badge status-pending">
            Available
          </div>
        </div>
        <div class="empty-feed">
          <sl-icon name="geo-alt" style="font-size: 3rem; color: #ddd; margin-bottom: 1rem;"></sl-icon>
          <p style="margin-bottom: 1rem;">No location set</p>
          <sl-input id="location-input-${slot}" placeholder="City, State ZIP (e.g., Seattle, WA 98101)" size="medium" style="width: 100%; max-width: 300px;"></sl-input>
          <p style="font-size: 0.85rem; color: #666; margin-top: 0.5rem;">Enter your location as "City, State ZIP"</p>
          <sl-button id="set-location-${slot}" variant="primary" size="medium" style="margin-top: 1rem; width: 100%; max-width: 200px;">
            <sl-icon name="plus-circle" slot="prefix"></sl-icon>
            Set Location
          </sl-button>
        </div>
      `;

      // Add event listener after element is added to DOM
      setTimeout(() => {
        const btn = card.querySelector(`#set-location-${slot}`) as HTMLElement;
        const input = card.querySelector(`#location-input-${slot}`) as HTMLInputElement;
        btn?.addEventListener('click', () => setLocation(slot, input.value));
      }, 0);
    }
  } else {
    // Active feed
    const baseUrl = window.location.origin;
    const icalUrl = `${baseUrl}/api/feeds/${feed.id}/ical`;
    const rssUrl = `${baseUrl}/api/feeds/${feed.id}/rss`;
    const webcalUrl = `webcal://${window.location.host}/api/feeds/${feed.id}/ical`;

    // Format dates nicely
    const formatDate = (dateStr: string | null | undefined) => {
      if (!dateStr) return 'Never';
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} min ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    };

    const tierBadge = feed.feed_tier === 'premium' 
      ? '<sl-badge variant="success">Premium</sl-badge>'
      : '<sl-badge variant="neutral">Basic</sl-badge>';

    card.innerHTML = `
      <div class="feed-header">
        <div class="feed-title">Feed ${slot}</div>
        <div style="display: flex; gap: 0.5rem; align-items: center;">
          ${tierBadge}
          <div class="status-badge status-active">
            <sl-icon name="check-circle" style="font-size: 0.75rem;"></sl-icon>
            Active
          </div>
          <sl-button id="refresh-feed-${slot}" variant="default" size="small" title="Refresh weather data">
            <sl-icon name="arrow-clockwise"></sl-icon>
          </sl-button>
        </div>
      </div>
      <div class="feed-location">
        <sl-icon name="geo-alt-fill" style="color: #764ba2;"></sl-icon>
        <strong id="location-text-${slot}">${feed.location}</strong>
        <input id="location-edit-${slot}" value="${feed.location}" placeholder="City, State ZIP" style="display: none; flex: 1; padding: 0.25rem 0.5rem; border: 1px solid #667eea; border-radius: 4px; font-weight: 600;" />
        ${feed.latitude && feed.longitude ? `
          <a href="https://www.google.com/maps/search/?api=1&query=${feed.latitude},${feed.longitude}" 
             target="_blank" 
             title="View on Google Maps"
             style="display: inline-flex; align-items: center; margin-left: 0.5rem; color: #667eea; text-decoration: none; padding: 0.25rem;">
            <sl-icon name="map" style="font-size: 1rem;"></sl-icon>
          </a>
        ` : ''}
        <sl-icon-button id="edit-location-${slot}" name="pencil" label="Edit location" style="font-size: 0.875rem; margin-left: 0.5rem;"></sl-icon-button>
      </div>
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem; margin: 0.75rem 0; padding: 0.5rem; background: #f8f9fa; border-radius: 6px; font-size: 0.75rem; color: #666; overflow-wrap: break-word;">
        <div style="word-break: break-word;">
          <strong>iCal Access:</strong><br>
          ${feed.ical_access_count || 0} times ${feed.ical_last_accessed ? `(${formatDate(feed.ical_last_accessed)})` : ''}
        </div>
        <div style="word-break: break-word;">
          <strong>Feed Type:</strong><br>
          ${feed.feed_tier === 'premium' ? '7-day w/ history' : '3-day basic'}
        </div>
        <div style="word-break: break-word;">
          <strong>Last Refreshed:</strong><br>
          ${formatDate(feed.last_refreshed)}
        </div>
        <div style="word-break: break-word;">
          <strong>Feed Refresh Count:</strong><br>
          ${feed.refresh_count || 0} times
        </div>
      </div>
      <div class="feed-urls">
        <div class="feed-url-group">
          <div class="feed-url-label">📅 iCal Feed</div>
          <div class="feed-url-input">
            <input class="url-field" id="ical-url-${slot}" value="${icalUrl}" readonly />
            <sl-button size="small" variant="default" id="copy-ical-${slot}">
              <sl-icon name="clipboard"></sl-icon>
            </sl-button>
            <sl-button size="small" variant="success" id="add-to-calendar-${slot}">
              <sl-icon name="calendar-plus"></sl-icon>
              Add to Calendar
            </sl-button>
          </div>
        </div>
        <!--
        <div class="feed-url-group">
          <div class="feed-url-label">📰 RSS Feed</div>
          <div class="feed-url-input">
            <input class="url-field" id="rss-url-${slot}" value="${rssUrl}" readonly />
            <sl-button size="small" variant="default" id="copy-rss-${slot}">
              <sl-icon name="clipboard"></sl-icon>
            </sl-button>
          </div>
        </div>
        -->
      </div>
      <div id="forecast-container-${slot}" style="margin-top: 1rem; padding-top: 1rem; border-top: 2px solid #f0f0f0;">
        <!-- Forecast will be loaded here -->
      </div>
      <sl-button id="delete-feed-${slot}" variant="danger" size="small" style="margin-top: 1rem; width: 100%;" outline>
        <sl-icon name="trash" slot="prefix"></sl-icon>
        Delete Feed
      </sl-button>
    `;

    // Add event listeners
    setTimeout(() => {
      // Copy buttons
      const icalField = card.querySelector(`#ical-url-${slot}`) as HTMLInputElement;
      const rssField = card.querySelector(`#rss-url-${slot}`) as HTMLInputElement;

      card.querySelector(`#copy-ical-${slot}`)?.addEventListener('click', () => {
        copyToClipboard(icalField.value);
      });

      card.querySelector(`#copy-rss-${slot}`)?.addEventListener('click', () => {
        copyToClipboard(rssField.value);
      });

      // Click on field to copy
      icalField?.addEventListener('click', () => copyToClipboard(icalField.value));
      rssField?.addEventListener('click', () => copyToClipboard(rssField.value));

      // Add to Calendar button
      card.querySelector(`#add-to-calendar-${slot}`)?.addEventListener('click', () => {
        window.location.href = webcalUrl;
      });

      // Location edit functionality
      const locationText = card.querySelector(`#location-text-${slot}`) as HTMLElement;
      const locationEdit = card.querySelector(`#location-edit-${slot}`) as HTMLInputElement;
      const editBtn = card.querySelector(`#edit-location-${slot}`) as HTMLElement;

      editBtn?.addEventListener('click', () => {
        locationText.style.display = 'none';
        locationEdit.style.display = 'block';
        editBtn.style.display = 'none';
        locationEdit.focus();
        locationEdit.select();
      });

      const saveLocation = async () => {
        const newLocation = locationEdit.value.trim();
        if (newLocation && newLocation !== feed.location) {
          // Show loading state on the edit button
          const editBtnEl = editBtn as HTMLElement & { loading: boolean };
          if (editBtnEl) {
            editBtnEl.loading = true;
          }
          
          try {
            await updateFeedLocation(feed.id as string, newLocation);
            locationText.textContent = newLocation;
            feed.location = newLocation;
            showToast('Location updated successfully!', 'success');

            // Hide edit mode on success
            locationText.style.display = 'inline';
            locationEdit.style.display = 'none';
            editBtn.style.display = 'inline';
            await loadFeeds();
          } catch (error: unknown) {
            // Check if it's a validation error with a specific message
            if (error instanceof ApiError && error.status === 400 && typeof error.body === 'object' && error.body?.detail) {
              showToast(error.body.detail, 'warning');
            } else {
              showToast('Failed to update location. Please try a format like "City, State ZIP" (e.g., "Seattle, WA 98101").', 'danger');
            }
            // Keep edit mode open and select text for retry
            locationEdit.focus();
            locationEdit.select();
          } finally {
            // Remove loading state
            if (editBtnEl) {
              editBtnEl.loading = false;
            }
          }
        } else {
          // No change or empty, just cancel edit
          locationText.style.display = 'inline';
          locationEdit.style.display = 'none';
          editBtn.style.display = 'inline';
        }
      };

      locationEdit?.addEventListener('blur', saveLocation);
      locationEdit?.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          saveLocation();
        }
      });

      // Delete button
      const deleteBtn = card.querySelector(`#delete-feed-${slot}`) as HTMLElement;
      deleteBtn?.addEventListener('click', () => deleteFeed(feed.id as string));

      // Refresh button
      const refreshBtn = card.querySelector(`#refresh-feed-${slot}`) as HTMLElement;
      refreshBtn?.addEventListener('click', async () => {
        await refreshFeed(feed.id as string, slot);
      });

      // Load forecast if feed has last_refreshed date
      if (feed.last_refreshed && feed.id) {
        loadFeedForecast(feed.id, slot);
      }
    }, 0);
  }

  return card;
}

// Set location for a feed slot
async function setLocation(slot: number, location: string) {
  if (!location.trim()) {
    showToast('Please enter a location', 'warning');
    return;
  }

  // Show loading state on the button
  const btn = document.querySelector(`#set-location-${slot}`) as HTMLElement & { loading: boolean };
  if (btn) {
    btn.loading = true;
  }

  try {
    const response = await apiCall<Feed>('POST', '/api/feeds', {
      json: { slot, location },
    });
    showToast('Location set successfully!', 'success');
    
    // Automatically refresh the feed after creation
    if (response && response.id) {
      try {
        await apiCall('POST', `/api/process-feed/${response.id}`);
        showToast('Feed refreshed with initial data!', 'success');
      } catch (refreshError) {
        console.error('Failed to refresh feed after creation:', refreshError);
        // Don't show error toast since the feed was created successfully
      }
    }
    
    await loadFeeds();
  } catch (error: unknown) {
    // Check if it's a validation error with a specific message
    if (error instanceof ApiError && error.status === 400 && typeof error.body === 'object' && error.body?.detail) {
      showToast(error.body.detail, 'warning');
    } else {
      showToast('Failed to set location. Please try a format like "City, State ZIP" (e.g., "Seattle, WA 98101").', 'danger');
    }
    // Keep the input value so user can modify it
    const input = document.querySelector(`#location-input-${slot}`) as HTMLInputElement;
    if (input) {
      input.focus();
      input.select();
    }
  } finally {
    // Remove loading state
    if (btn) {
      btn.loading = false;
    }
  }
}

// Update feed location
async function updateFeedLocation(feedId: string, location: string) {
  const response = await apiCall('PATCH', `/api/feeds/${feedId}`, {
    json: { location },
  });
  return response;
}

// Delete a feed
async function deleteFeed(feedId: string) {
  const confirmDialog = confirm('Are you sure? This will permanently delete this feed.');
  if (confirmDialog) {
    try {
      await apiCall('DELETE', `/api/feeds/${feedId}`);
      showToast('Feed deleted', 'success');
      await loadFeeds();
    } catch {
      showToast('Failed to delete feed', 'danger');
    }
  }
}

// Refresh a feed
async function refreshFeed(feedId: string, slot: number) {
  const refreshBtn = document.querySelector(`#refresh-feed-${slot}`) as HTMLElement & { loading: boolean };
  
  try {
    // Show loading state
    if (refreshBtn) {
      refreshBtn.loading = true;
    }
    
    await apiCall('POST', `/api/process-feed/${feedId}`);
    showToast('Feed refreshed successfully!', 'success');
    
    // Reload the feeds to show updated data
    await loadFeeds();
  } catch (error: unknown) {
    if (error instanceof ApiError && error.status === 429) {
      // Rate limited - extract the message from the error body
      const message = (typeof error.body === 'object' && error.body?.detail) || 'Feed was recently updated. Please wait before refreshing again.';
      showToast(message, 'warning');
    } else if (error instanceof ApiError && error.status === 403) {
      showToast('You can only refresh your own feeds', 'danger');
    } else {
      showToast('Failed to refresh feed', 'danger');
    }
  } finally {
    // Remove loading state
    if (refreshBtn) {
      refreshBtn.loading = false;
    }
  }
}

// Copy to clipboard helper
function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).then(() => {
    showToast('Copied to clipboard!', 'success');
  }).catch(() => {
    showToast('Failed to copy', 'danger');
  });
}

async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Show toast notification
async function showToast(message: string, variant: string = 'primary') {
  // Create the alert element using Shoelace's custom element
  const alert = document.createElement('sl-alert') as HTMLElement & {variant: string; closable: boolean; duration: number; toast: () => Promise<void>};
  alert.variant = variant;
  alert.closable = true;
  alert.duration = 3000;
  
  // Set the icon based on variant
  const iconMap: Record<string, string> = {
    primary: 'info-circle',
    success: 'check2-circle', 
    warning: 'exclamation-triangle',
    danger: 'exclamation-octagon',
    neutral: 'gear',
  };
  
  alert.innerHTML = `
    <sl-icon slot="icon" name="${iconMap[variant] || 'info-circle'}"></sl-icon>
    ${message}
  `;

  document.body.appendChild(alert);
  await sleep(50);
  
  // Call toast() method after the element is added to DOM
  return alert.toast();
}